import React from "react";
import { graphql, useLazyLoadQuery, usePaginationFragment } from "react-relay";

import {
	faEllipsisV,
	faPlus,
	faPlusCircle,
	faRepeat,
	faShareAll,
	faTrashCan,
} from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Loader } from "semantic-ui-react";

import type { EventFormResult } from "~/calendar/EventForm";
import { dispatchAnalyticsTrack } from "~/lib/segment";
import { withSuspense } from "~/lib/suspense";
import { reduceEdgesToNodes } from "~/lib/utils";
import { EventFormModalWithoutViewer } from "~/nav/panels/Meetings/EventFormModal/EventFormModal";
import { recurrenceRuleToString } from "~/stream/utils";
import { Button, toast, Tooltip, useModals } from "~/ui";
import Avatar from "~/ui/legacy/Avatar";
import { ButtonLink, IconButton } from "~/ui/legacyFellowIndex";
import Menu from "~/ui/menu";

import { AddDirectReportModal } from "./AddDirectReportModal";
import { RemoveDirectReportModal } from "./RemoveDirectReportModal";
import { TransferDirectReportModal } from "./TransferDirectReportModal";

import type { DirectReports_reportInfo$data as Report } from "~/__generated__/DirectReports_reportInfo.graphql";
import type { DirectReportsPaginationQuery } from "~/__generated__/DirectReportsPaginationQuery.graphql";
import type { DirectReportsQuery } from "~/__generated__/DirectReportsQuery.graphql";
import type { DirectReportsUser$key } from "~/__generated__/DirectReportsUser.graphql";

import styles from "./css/index.module.css";

graphql`
	fragment DirectReports_reportInfo on User {
		id
		fullName
		email
		isGuest
		activeRecurring1on1Event {
			start
			recurrenceRule
		}
	}
`;

const PAGE_SIZE = 10;

const DirectReportsPaginationFragment = graphql`
	fragment DirectReportsUser on User
	@argumentDefinitions(first: { type: "Int" }, after: { type: "String" })
	@refetchable(queryName: "DirectReportsPaginationQuery") {
		internalAttributes {
			data: directReports(first: $first, after: $after, includeGuests: true) @connection(key: "DirectReports_data") {
				edges {
					node {
						...DirectReports_reportInfo @relay(mask: false)
					}
				}
			}
		}
	}
`;

export type DirectReport = NonNullable<Omit<Report, " $fragmentType">>;

function MeetingTime({ report, onAddEvent }: { report: DirectReport; onAddEvent: () => void }) {
	const { activeRecurring1on1Event } = report;
	const { start, recurrenceRule } = activeRecurring1on1Event ?? {};

	const nextMeetingDate = start ? new Date(start) : null;
	const recurrence = recurrenceRule ? recurrenceRuleToString(recurrenceRule) : null;

	const meetingTime = (date: Date) => (
		<div className={styles.recurrence}>
			<FontAwesomeIcon icon={faRepeat} style={{ marginRight: "4px" }} />
			<p>
				{new Intl.DateTimeFormat("en-US", {
					weekday: "short",
					hour: "numeric",
					minute: "numeric",
				}).format(date)}
			</p>
		</div>
	);

	return nextMeetingDate ? (
		<>
			{recurrence ? (
				<Tooltip placement="top" content={recurrence}>
					{meetingTime(nextMeetingDate)}
				</Tooltip>
			) : (
				meetingTime(nextMeetingDate)
			)}
		</>
	) : (
		<ButtonLink className={styles.link} onClick={onAddEvent}>
			<FontAwesomeIcon icon={faPlus} style={{ marginRight: "4px" }} />
			Add Recurring
		</ButtonLink>
	);
}

interface ReportRowProps {
	report: DirectReport;
	refetch: () => void;
	excludedEmails: string[];
	excludedUserIds: string[];
	internalInvitesAllowed: boolean;
	guestInvitesAllowed: boolean;
}

function ReportRow({
	report,
	refetch,
	excludedEmails,
	excludedUserIds,
	internalInvitesAllowed,
	guestInvitesAllowed,
}: ReportRowProps) {
	const modals = useModals();

	const onRemoveReport = React.useCallback(() => {
		modals.open(({ close }) => {
			return <RemoveDirectReportModal onClose={close} report={report} refetch={refetch} />;
		});
	}, [modals, refetch, report]);

	const onTransferReport = React.useCallback(() => {
		modals.open(({ close }) => {
			return (
				<TransferDirectReportModal
					onClose={close}
					report={report}
					refetch={refetch}
					excludedEmails={excludedEmails}
					excludedUserIds={excludedUserIds}
					internalInvitesAllowed={internalInvitesAllowed}
					guestInvitesAllowed={guestInvitesAllowed}
				/>
			);
		});
	}, [modals, refetch, report, excludedEmails, excludedUserIds, internalInvitesAllowed, guestInvitesAllowed]);

	const onEventFormSaved = React.useCallback(
		(closeModal: () => void) => (result: EventFormResult) => {
			switch (result.type) {
				case "cancelled":
					return closeModal();
				case "succeeded": {
					refetch();
					dispatchAnalyticsTrack("[Event Creation Modal] Created Event", {
						// eslint-disable-next-line camelcase
						ux_location: "settings__direct_reports",
						action: "created_recurring_1on1",
					});
					toast.success(`Recurring 1-on-1 with ${report.fullName} was created.`);
					return closeModal();
				}
				default:
					return;
			}
		},
		[refetch, report.fullName],
	);

	const openAddEventModal = React.useCallback(() => {
		dispatchAnalyticsTrack("[Event Creation Modal] Opened Modal", {
			// eslint-disable-next-line camelcase
			ux_location: "settings__direct_reports",
			action: "setup_recurring_1on1",
		});

		modals.open(({ close }) => (
			<React.Suspense fallback={null}>
				<EventFormModalWithoutViewer
					onFormFinish={onEventFormSaved(close)}
					initialData={{
						repeat: "BIWEEKLY",
						participants: [report],
						purpose:
							// eslint-disable-next-line max-len
							"The purpose of this 1-on-1 meeting is to review recent successes and challenges, provide feedback, and to set professional development goals.",
					}}
				/>
			</React.Suspense>
		));
	}, [modals, onEventFormSaved, report]);

	return (
		<div className={styles.tableRow}>
			<div className={styles.tableRow__avatar}>
				<Avatar style={{ margin: 0 }} user={report} width="24px" />
			</div>

			<div className={styles.tableRow__nameRecurrence}>
				<div className={styles.tableRow__nameRecurrence__name}>{report.fullName}</div>
				<div className={styles.tableRow__nameRecurrence__recurrence}>
					<MeetingTime report={report} onAddEvent={openAddEventModal} />
				</div>
			</div>

			<Menu>
				<Menu.Trigger>
					<IconButton icon={faEllipsisV} className={styles.tableRow__menuButton} />
				</Menu.Trigger>
				<Menu.Content data-ignore-focus-lock side="bottom" align="end">
					<Menu.Item icon={faShareAll} onSelect={onTransferReport}>
						Transfer direct report
					</Menu.Item>
					<Menu.Separator />
					<Menu.Item destructive icon={faTrashCan} onSelect={onRemoveReport}>
						Remove as direct report
					</Menu.Item>
				</Menu.Content>
			</Menu>
		</div>
	);
}

export function DirectReportsRenderer() {
	const { viewer } = useLazyLoadQuery<DirectReportsQuery>(
		graphql`
			query DirectReportsQuery($first: Int, $after: String) {
				viewer {
					account {
						allowInviting
						guestUsersAllowInvite
					}
					user {
						id
						email
						internalAttributes {
							manager {
								id
								email
							}
						}

						...DirectReportsUser @arguments(first: $first, after: $after)
					}
				}
			}
		`,
		{ first: PAGE_SIZE },
		{ fetchPolicy: "store-and-network" },
	);

	const { data, loadNext, hasNext, refetch } = usePaginationFragment<
		DirectReportsPaginationQuery,
		DirectReportsUser$key
	>(DirectReportsPaginationFragment, viewer.user);

	const { user, account } = viewer;

	const reports = reduceEdgesToNodes(data.internalAttributes?.data?.edges);

	const onLoadMore = React.useCallback(() => loadNext(PAGE_SIZE), [loadNext]);

	const refetchReports = React.useCallback(() => {
		refetch({ first: PAGE_SIZE }, { fetchPolicy: "store-and-network" });
	}, [refetch]);

	const modals = useModals();
	const openDirectReportModal = React.useCallback(() => {
		modals.open(({ close }) => {
			return (
				<AddDirectReportModal
					onClose={close}
					refetch={refetchReports}
					userId={user.id}
					excludedEmails={[
						user.email,
						...(user.internalAttributes?.manager ? [user.internalAttributes.manager.email] : []),
					]}
					excludedUserIds={[user.id, ...(user.internalAttributes?.manager ? [user.internalAttributes.manager.id] : [])]}
					internalInvitesAllowed={account.allowInviting ?? false}
					guestInvitesAllowed={account.guestUsersAllowInvite ?? false}
				/>
			);
		});
	}, [modals, refetchReports, user, account]);

	return (
		<div className={styles.container}>
			{reports.length > 0 ? (
				<div className={styles.tableWrapper}>
					<div>
						<div className={styles.tableHeader}>
							<div>Direct report</div>
							<div>Recurring 1-on-1</div>
						</div>

						{reports.map(report => (
							<ReportRow
								key={report.id}
								report={report}
								refetch={refetchReports}
								excludedEmails={[user.email, report.email]}
								excludedUserIds={[user.id, report.id]}
								internalInvitesAllowed={account.allowInviting ?? false}
								guestInvitesAllowed={account.guestUsersAllowInvite ?? false}
							/>
						))}
					</div>

					{hasNext && (
						<ButtonLink className={styles.loadMore} onClick={onLoadMore}>
							See more
						</ButtonLink>
					)}
				</div>
			) : (
				<div className={styles.noReports}>You have 0 direct reports set</div>
			)}
			<Button className={styles.addReport} onClick={openDirectReportModal}>
				<FontAwesomeIcon icon={faPlusCircle} className={styles.icon} /> Add direct report
			</Button>
		</div>
	);
}

export const DirectReports = withSuspense(<Loader active inline="centered" />, DirectReportsRenderer);
